import type { ISupplierRepository } from '@yawi/domain';
import type { PaginatedResponse } from '@yawi/domain';
import type {
  Supplier,
  SupplierParams,
  CreateSupplierDTO,
  UpdateSupplierDTO,
  SupplierEmployee,
  CreateEmployeeDTO,
  UpdateEmployeeDTO,
  EmployeeQRResponse,
  EmployeeByQRResponse,
  SupplierVisit,
  SupplierVisitParams,
  RegisterVisitDTO,
  CheckoutVisitDTO,
  RateVisitDTO,
} from '@yawi/types';
import { getApiClient } from '../../http/apiClient';

export class ApiSupplierRepository implements ISupplierRepository {
  private readonly basePath = 'v1/suppliers';

  // ========== SUPPLIERS CRUD ==========

  async getSuppliers(params?: SupplierParams): Promise<PaginatedResponse<Supplier>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.search) queryParams.set('search', params.search);
    if (params?.category) queryParams.set('category', params.category);
    if (params?.activeOnly !== undefined) queryParams.set('activeOnly', String(params.activeOnly));
    if (params?.sortBy) queryParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.set('sortOrder', params.sortOrder);

    const queryString = queryParams.toString();
    const url = `${this.basePath}${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<Supplier>>(url);
  }

  async getSupplierById(id: string): Promise<Supplier> {
    const client = getApiClient();
    return await client.get<Supplier>(`${this.basePath}/${id}`);
  }

  async createSupplier(data: CreateSupplierDTO): Promise<Supplier> {
    const client = getApiClient();
    return await client.post<Supplier>(this.basePath, data);
  }

  async updateSupplier(id: string, data: UpdateSupplierDTO): Promise<Supplier> {
    const client = getApiClient();
    return await client.put<Supplier>(`${this.basePath}/${id}`, data);
  }

  async deleteSupplier(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.basePath}/${id}`);
  }

  // ========== EMPLOYEES ==========

  async getEmployees(supplierId: string, activeOnly: boolean = true): Promise<SupplierEmployee[]> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();
    queryParams.set('activeOnly', String(activeOnly));

    return await client.get<SupplierEmployee[]>(
      `${this.basePath}/${supplierId}/employees?${queryParams.toString()}`
    );
  }

  async createEmployee(supplierId: string, data: CreateEmployeeDTO): Promise<SupplierEmployee> {
    const client = getApiClient();
    return await client.post<SupplierEmployee>(
      `${this.basePath}/${supplierId}/employees`,
      data
    );
  }

  async updateEmployee(supplierId: string, employeeId: string, data: UpdateEmployeeDTO): Promise<SupplierEmployee> {
    const client = getApiClient();
    return await client.put<SupplierEmployee>(
      `${this.basePath}/${supplierId}/employees/${employeeId}`,
      data
    );
  }

  async deleteEmployee(supplierId: string, employeeId: string): Promise<void> {
    const client = getApiClient();
    await client.delete(`${this.basePath}/${supplierId}/employees/${employeeId}`);
  }

  async getEmployeeQR(supplierId: string, employeeId: string): Promise<EmployeeQRResponse> {
    const client = getApiClient();
    return await client.get<EmployeeQRResponse>(
      `${this.basePath}/${supplierId}/employees/${employeeId}/qr`
    );
  }

  async getEmployeeByQR(qrCode: string): Promise<EmployeeByQRResponse> {
    const client = getApiClient();
    return await client.get<EmployeeByQRResponse>(
      `${this.basePath}/employees/by-qr/${qrCode}`
    );
  }

  // ========== VISITS ==========

  async getVisits(supplierId: string, params?: SupplierVisitParams): Promise<PaginatedResponse<SupplierVisit>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.startDate) queryParams.set('dateFrom', params.startDate);
    if (params?.endDate) queryParams.set('dateTo', params.endDate);
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/${supplierId}/visits${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<SupplierVisit>>(url);
  }

  async getAllVisits(params?: SupplierVisitParams): Promise<PaginatedResponse<SupplierVisit>> {
    const client = getApiClient();
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.set('page', String(params.page));
    if (params?.limit) queryParams.set('limit', String(params.limit));
    if (params?.supplierId) queryParams.set('supplierId', params.supplierId);
    // El DTO del backend los llama dateFrom/dateTo; con startDate/endDate el
    // filtro de fecha se ignoraba en silencio.
    if (params?.startDate) queryParams.set('dateFrom', params.startDate);
    if (params?.endDate) queryParams.set('dateTo', params.endDate);
    if (params?.search) queryParams.set('search', params.search);

    const queryString = queryParams.toString();
    const url = `${this.basePath}/visits${queryString ? `?${queryString}` : ''}`;

    return await client.get<PaginatedResponse<SupplierVisit>>(url);
  }

  async registerVisit(supplierId: string, data: RegisterVisitDTO): Promise<SupplierVisit> {
    const client = getApiClient();
    return await client.post<SupplierVisit>(
      `${this.basePath}/${supplierId}/visits`,
      data
    );
  }

  async checkoutVisit(supplierId: string, visitId: string, data?: CheckoutVisitDTO): Promise<SupplierVisit> {
    const client = getApiClient();
    return await client.post<SupplierVisit>(
      `${this.basePath}/${supplierId}/visits/${visitId}/checkout`,
      data || {}
    );
  }

  async rateVisit(supplierId: string, visitId: string, data: RateVisitDTO): Promise<SupplierVisit> {
    const client = getApiClient();
    return await client.post<SupplierVisit>(
      `${this.basePath}/${supplierId}/visits/${visitId}/rate`,
      data
    );
  }
}
